import { createTheme } from '@mui/material/styles';

// Synaptix Pulse Brand Colors
const pulseColors = {
    // Master Brand Colors
    synaptixBlue: '#2563ff',
    velocityPurple: '#6b4dff',
    signalWhite: '#ffffff',
    graphite: '#1c1c1e',

    // Product Accent Colors
    shiftTeal: '#16c79a',
    pulseAmber: '#f5a623',

    // Blue Ramp
    blue100: '#ebf2ff',
    blue200: '#cddfff',
    blue300: '#aabfff',
    blue400: '#8ba4ff',
    blue500: '#2563ff',
    blue600: '#1e54d9',
    blue700: '#1742b3',
    blue800: '#102d8c',
    blue900: '#0a1d66',

    // Teal Ramp
    teal100: '#e6faf4',
    teal200: '#c2f2e5',
    teal300: '#99e6d4',
    teal400: '#75dbca',
    teal500: '#16c79a',
    teal600: '#139e7e',
    teal700: '#0f795d',
    teal800: '#0b523f',
    teal900: '#062f24',

    // Amber Ramp (Pulse Primary)
    amber100: '#fff8eb',
    amber200: '#ffecbf',
    amber300: '#ffdf94',
    amber400: '#ffd469',
    amber500: '#f5a623',
    amber600: '#cc891c',
    amber700: '#a36315',
    amber800: '#7a4a0f',
    amber900: '#523208',

    // Purple Ramp
    purple100: '#ede9ff',
    purple200: '#d5ccff',
    purple300: '#b9adff',
    purple400: '#9d8dff',
    purple500: '#6b4dff',
    purple600: '#5640d9',
    purple700: '#4633b3',
    purple800: '#35268c',
    purple900: '#241b66',
};

// Pulse Theme Colors
const colors = {
    // Primary brand colors (Pulse Amber)
    primary: {
        50: pulseColors.amber100,
        100: pulseColors.amber200,
        200: pulseColors.amber300,
        300: pulseColors.amber400,
        400: pulseColors.amber400,
        500: pulseColors.pulseAmber, // Main Pulse color
        600: pulseColors.amber600,
        700: pulseColors.amber700,
        800: pulseColors.amber800,
        900: pulseColors.amber900,
        main: pulseColors.pulseAmber,
        contrastText: pulseColors.signalWhite,
    },

    // Secondary accent colors (Synaptix Blue)
    secondary: {
        50: pulseColors.blue100,
        100: pulseColors.blue200,
        200: pulseColors.blue300,
        300: pulseColors.blue400,
        400: pulseColors.blue400,
        500: pulseColors.synaptixBlue,
        600: pulseColors.blue600,
        700: pulseColors.blue700,
        800: pulseColors.blue800,
        900: pulseColors.blue900,
        main: pulseColors.synaptixBlue,
        contrastText: pulseColors.signalWhite,
    },

    // Status colors with Pulse branding
    success: {
        50: pulseColors.teal100,
        100: pulseColors.teal200,
        200: pulseColors.teal300,
        300: pulseColors.teal400,
        400: pulseColors.teal400,
        500: pulseColors.shiftTeal,
        600: pulseColors.teal600,
        700: pulseColors.teal700,
        800: pulseColors.teal800,
        900: pulseColors.teal900,
        main: pulseColors.shiftTeal,
        contrastText: pulseColors.signalWhite,
    },

    warning: {
        50: pulseColors.amber100,
        100: pulseColors.amber200,
        200: pulseColors.amber300,
        300: pulseColors.amber400,
        400: pulseColors.amber400,
        500: pulseColors.pulseAmber,
        600: pulseColors.amber600,
        700: pulseColors.amber700,
        800: pulseColors.amber800,
        900: pulseColors.amber900,
        main: pulseColors.pulseAmber,
        contrastText: '#000000',
    },

    error: {
        50: '#ffebee',
        100: '#ffcdd2',
        200: '#ef9a9a',
        300: '#e57373',
        400: '#ef5350',
        500: '#f44336',
        600: '#e53935',
        700: '#d32f2f',
        800: '#c62828',
        900: '#b71c1c',
        main: '#f44336',
        contrastText: pulseColors.signalWhite,
    },

    // Neutral grays
    neutral: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#eeeeee',
        300: '#e0e0e0',
        400: '#bdbdbd',
        500: '#9e9e9e',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
    },

    // Background colors
    background: {
        default: pulseColors.signalWhite,
        paper: pulseColors.signalWhite,
        elevated: pulseColors.signalWhite,
        overlay: 'rgba(0, 0, 0, 0.5)',
    },

    // Text colors
    text: {
        primary: pulseColors.graphite,
        secondary: 'rgba(28, 28, 30, 0.7)',
        disabled: 'rgba(28, 28, 30, 0.38)',
        hint: 'rgba(28, 28, 30, 0.38)',
    },
};

// Inter Tight Typography (Pulse Brand Font)
const typography = {
    fontFamily: [
        'Inter Tight',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
    ].join(','),

    // 8-pt grid typography scale
    h1: {
        fontSize: '3.5rem', // 56px
        fontWeight: 600,
        lineHeight: 1.25,
        letterSpacing: '-0.025em',
        color: pulseColors.synaptixBlue,
    },
    h2: {
        fontSize: '2.5rem', // 40px
        fontWeight: 600,
        lineHeight: 1.25,
        letterSpacing: '-0.025em',
    },
    h3: {
        fontSize: '2rem', // 32px
        fontWeight: 600,
        lineHeight: 1.25,
    },
    h4: {
        fontSize: '1.5rem', // 24px
        fontWeight: 500,
        lineHeight: 1.25,
    },
    h5: {
        fontSize: '1.25rem', // 20px
        fontWeight: 500,
        lineHeight: 1.5,
    },
    h6: {
        fontSize: '1rem', // 16px
        fontWeight: 500,
        lineHeight: 1.5,
    },
    subtitle1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
    },
    subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
    },
    body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
    },
    body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.43,
        letterSpacing: '0.01071em',
    },
    button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: '0.025em',
        textTransform: 'uppercase',
    },
    caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
    },
    overline: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 2.66,
        letterSpacing: '0.08333em',
        textTransform: 'uppercase',
    },
};

// Spacing system based on 8px grid
const spacing = {
    unit: 8,
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
};

// Pulse-specific shadows with amber tint
const shadows = [
    'none',
    '0px 2px 1px -1px rgba(245, 166, 35, 0.1),0px 1px 1px 0px rgba(245, 166, 35, 0.07),0px 1px 3px 0px rgba(245, 166, 35, 0.06)',
    '0px 3px 1px -2px rgba(245, 166, 35, 0.1),0px 2px 2px 0px rgba(245, 166, 35, 0.07),0px 1px 5px 0px rgba(245, 166, 35, 0.06)',
    '0px 3px 3px -2px rgba(245, 166, 35, 0.1),0px 3px 4px 0px rgba(245, 166, 35, 0.07),0px 1px 8px 0px rgba(245, 166, 35, 0.06)',
    '0px 2px 4px -1px rgba(245, 166, 35, 0.1),0px 4px 5px 0px rgba(245, 166, 35, 0.07),0px 1px 10px 0px rgba(245, 166, 35, 0.06)',
    '0px 3px 5px -1px rgba(245, 166, 35, 0.1),0px 5px 8px 0px rgba(245, 166, 35, 0.07),0px 1px 14px 0px rgba(245, 166, 35, 0.06)',
    '0px 3px 5px -1px rgba(245, 166, 35, 0.1),0px 6px 10px 0px rgba(245, 166, 35, 0.07),0px 1px 18px 0px rgba(245, 166, 35, 0.06)',
    '0px 4px 5px -2px rgba(245, 166, 35, 0.1),0px 7px 10px 1px rgba(245, 166, 35, 0.07),0px 2px 16px 1px rgba(245, 166, 35, 0.06)',
    '0px 5px 5px -3px rgba(245, 166, 35, 0.1),0px 8px 10px 1px rgba(245, 166, 35, 0.07),0px 3px 14px 2px rgba(245, 166, 35, 0.06)',
    '0px 5px 6px -3px rgba(245, 166, 35, 0.1),0px 9px 12px 1px rgba(245, 166, 35, 0.07),0px 3px 16px 2px rgba(245, 166, 35, 0.06)',
    '0px 6px 6px -3px rgba(245, 166, 35, 0.1),0px 10px 14px 1px rgba(245, 166, 35, 0.07),0px 4px 18px 3px rgba(245, 166, 35, 0.06)',
    '0px 6px 7px -4px rgba(245, 166, 35, 0.1),0px 11px 15px 1px rgba(245, 166, 35, 0.07),0px 4px 20px 3px rgba(245, 166, 35, 0.06)',
    '0px 7px 8px -4px rgba(245, 166, 35, 0.1),0px 12px 17px 2px rgba(245, 166, 35, 0.07),0px 5px 22px 4px rgba(245, 166, 35, 0.06)',
    '0px 7px 8px -4px rgba(245, 166, 35, 0.1),0px 13px 19px 2px rgba(245, 166, 35, 0.07),0px 5px 24px 4px rgba(245, 166, 35, 0.06)',
    '0px 7px 9px -4px rgba(245, 166, 35, 0.1),0px 14px 21px 2px rgba(245, 166, 35, 0.07),0px 5px 26px 4px rgba(245, 166, 35, 0.06)',
    '0px 8px 9px -5px rgba(245, 166, 35, 0.1),0px 15px 22px 2px rgba(245, 166, 35, 0.07),0px 6px 28px 5px rgba(245, 166, 35, 0.06)',
    '0px 8px 10px -5px rgba(245, 166, 35, 0.1),0px 16px 24px 2px rgba(245, 166, 35, 0.07),0px 6px 30px 5px rgba(245, 166, 35, 0.06)',
    '0px 8px 11px -5px rgba(245, 166, 35, 0.1),0px 17px 26px 2px rgba(245, 166, 35, 0.07),0px 6px 32px 5px rgba(245, 166, 35, 0.06)',
    '0px 9px 11px -5px rgba(245, 166, 35, 0.1),0px 18px 28px 2px rgba(245, 166, 35, 0.07),0px 7px 34px 6px rgba(245, 166, 35, 0.06)',
    '0px 9px 12px -6px rgba(245, 166, 35, 0.1),0px 19px 29px 2px rgba(245, 166, 35, 0.07),0px 7px 36px 6px rgba(245, 166, 35, 0.06)',
    '0px 10px 13px -6px rgba(245, 166, 35, 0.1),0px 20px 31px 3px rgba(245, 166, 35, 0.07),0px 8px 38px 7px rgba(245, 166, 35, 0.06)',
    '0px 10px 13px -6px rgba(245, 166, 35, 0.1),0px 21px 33px 3px rgba(245, 166, 35, 0.07),0px 8px 40px 7px rgba(245, 166, 35, 0.06)',
    '0px 10px 14px -6px rgba(245, 166, 35, 0.1),0px 22px 35px 3px rgba(245, 166, 35, 0.07),0px 8px 42px 7px rgba(245, 166, 35, 0.06)',
    '0px 11px 14px -7px rgba(245, 166, 35, 0.1),0px 23px 36px 3px rgba(245, 166, 35, 0.07),0px 9px 44px 8px rgba(245, 166, 35, 0.06)',
    '0px 11px 15px -7px rgba(245, 166, 35, 0.1),0px 24px 38px 3px rgba(245, 166, 35, 0.07),0px 9px 46px 8px rgba(245, 166, 35, 0.06)',
];

// Breakpoints for responsive design
const breakpoints = {
    values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
    },
};

// Pulse motion tokens
const transitions = {
    duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
    },
    easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        elastic: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
};

// Z-index scale for layering
const zIndex = {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
};

export const pulseDesignTokens = {
    colors: pulseColors,
    typography,
    spacing,
    shadows,
    breakpoints,
    transitions,
    zIndex,
};

// Create the Pulse Material-UI theme
export const pulseTheme = createTheme({
    palette: {
        mode: 'light',
        primary: colors.primary,
        secondary: colors.secondary,
        error: colors.error,
        warning: colors.warning,
        info: colors.secondary,
        success: colors.success,
        background: colors.background,
        text: colors.text,
        grey: colors.neutral,
    },

    typography: {
        ...typography,
        htmlFontSize: 16,
    },

    spacing: spacing.unit,

    shadows,

    breakpoints,

    transitions: {
        duration: transitions.duration,
        easing: transitions.easing,
    },

    zIndex,

    shape: {
        borderRadius: 6, // Pulse uses 6px border radius
    },

    // Pulse-specific component overrides
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    minHeight: 44,
                    transition: 'all 0.25s cubic-bezier(0.0, 0, 0.2, 1)',
                    letterSpacing: '0.025em',
                    '&:hover': {
                        boxShadow: '0 8px 24px rgba(245, 166, 35, 0.3)',
                    },
                },
                contained: {
                    boxShadow: '0 2px 4px rgba(245, 166, 35, 0.1)',
                    '&:hover': {
                        boxShadow: '0 8px 24px rgba(245, 166, 35, 0.3)',
                    },
                },
                containedPrimary: {
                    backgroundColor: pulseColors.pulseAmber,
                    '&:hover': {
                        backgroundColor: pulseColors.amber600,
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },

        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(245, 166, 35, 0.1)',
                    transition: 'all 0.25s cubic-bezier(0.0, 0, 0.2, 1)',
                    border: `1px solid ${pulseColors.amber200}`,
                    '&:hover': {
                        boxShadow: '0 8px 24px rgba(245, 166, 35, 0.15)',
                        borderColor: pulseColors.pulseAmber,
                    },
                    '&:hover[data-clickable="true"]': {
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 6,
                        minHeight: 44,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: pulseColors.pulseAmber,
                        },
                    },
                },
            },
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                },
            },
        },

        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: pulseColors.graphite,
                    color: pulseColors.signalWhite,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
            },
        },

        MuiAlert: {
            styleOverrides: {
                standardInfo: {
                    // design-review #62: decorative left border on info "insight boxes"
                    borderLeft: '4px solid #2196F3',
                },
            },
        },

        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: `1px solid ${pulseColors.amber200}`,
                    boxShadow: 'none',
                    backgroundColor: pulseColors.signalWhite,
                },
            },
        },

        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    margin: '4px 8px',
                    minHeight: 44,
                    '&.Mui-selected': {
                        backgroundColor: pulseColors.amber100,
                        color: pulseColors.amber700,
                        '&:hover': {
                            backgroundColor: pulseColors.amber200,
                        },
                    },
                    '&:hover': {
                        backgroundColor: pulseColors.amber50,
                    },
                },
            },
        },

        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 44,
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    letterSpacing: '0.025em',
                    '&.Mui-selected': {
                        color: pulseColors.pulseAmber,
                    },
                },
            },
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    minWidth: 44,
                    minHeight: 44,
                    '&:hover': {
                        backgroundColor: pulseColors.amber50,
                    },
                },
            },
        },

        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    backgroundColor: pulseColors.amber100,
                    borderRadius: 4,
                },
                bar: {
                    backgroundColor: pulseColors.pulseAmber,
                    borderRadius: 4,
                },
            },
        },

        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: pulseColors.pulseAmber,
                },
            },
        },
    },
});

export default pulseTheme;
